import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import type { UIMessage } from 'ai'
import { useMemo } from 'react'
import { apiClient } from '@/shared/api/apiClient'
import { useGetUserSession } from './auth'

/** Matches server `SystemPromptRegistryKey` until shared package exports it. */
export type LlmChatSystemPromptKey = 'lex_chat_default'

export type LlmChatSessionDto = {
  id: string
  createdAt: string
  updatedAt: string
  organizationId: string
  userId: string | null
  productId: string | null
  systemPromptKey: string
  systemPromptVersion: string
  activeLeafMessageId: string | null
  summaryText: string | null
  summaryAnchorMessageId: string | null
  summaryRevision: number
  title: string | null
  topicMemo: string | null
  domainEstablished: boolean
}

export type LlmChatMessageRole = 'USER' | 'MODEL' | 'SYSTEM' | 'TOOL_RESULT'
export type LlmChatFeedbackRating = 'up' | 'down'
export type LlmChatFeedbackReason =
  | 'regulationNotCorrect'
  | 'linkFailed'
  | 'notHelpful'
  | 'questionNotAnswered'
  | 'other'

export type LlmChatMessageFeedbackDto = {
  rating: LlmChatFeedbackRating
  reason?: LlmChatFeedbackReason
  otherComment?: string
  createdAt: string
  updatedAt: string
  submittedByUserId: string
}

export type LlmChatMessageDto = {
  id: string
  createdAt: string
  updatedAt: string
  sessionId: string
  role: LlmChatMessageRole
  sequence: number
  parentMessageId: string | null
  contentText: string | null
  contentJson: unknown
  usageJson: unknown
  isComplete: boolean
  systemPromptKey: string | null
  systemPromptVersion: string | null
  modelId: string | null
  feedbackRating: LlmChatFeedbackRating | null
  feedbackReason: LlmChatFeedbackReason | null
  feedbackOtherComment: string | null
  feedbackSubmittedByUserId: string | null
  feedbackCreatedAt: string | null
  feedbackUpdatedAt: string | null
  toolInvocationIdForResult: string | null
  toolInvocationForResult?: {
    id: string
    externalInvocationRef: string | null
  } | null
}

export type CreateLlmChatSessionResponse = {
  session: LlmChatSessionDto
  allowedPromptKeys: string[]
}

export type GetLlmChatSessionResponse = {
  session: LlmChatSessionDto | null
  messages: LlmChatMessageDto[]
}

export type SendLlmChatMessageResponse = {
  session: LlmChatSessionDto | null
  userMessageId: string
  assistantText: string
  usage: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  turns: number
}

export type SubmitLlmChatMessageFeedbackPayload =
  | {
      rating: 'up'
    }
  | {
      rating: 'down'
      reason: LlmChatFeedbackReason
      otherComment?: string
    }

export type SubmitLlmChatMessageFeedbackResponse = {
  messageId: string
  feedback: LlmChatMessageFeedbackDto
}

export type ListLlmChatSessionsResponse = {
  sessions: LlmChatSessionDto[]
}

export const LLM_CHAT_SESSION_QUERY_ROOT = 'llmChat' as const

export function llmChatSessionsListQueryKey() {
  return [LLM_CHAT_SESSION_QUERY_ROOT, 'sessions', 'list'] as const
}

export function llmChatSessionQueryKey(sessionId: string) {
  return [LLM_CHAT_SESSION_QUERY_ROOT, 'session', sessionId] as const
}

export type LlmChatProviderInfoResponse = {
  providerId: string
  displayName: string
  models: {
    stateOfTheArtChat: string
    chatModelOptions: string[]
  }
}

export function llmChatProviderInfoQueryKey(
  organizationId: string | undefined,
  isArtemAdmin: boolean | undefined
) {
  return [
    LLM_CHAT_SESSION_QUERY_ROOT,
    'providerInfo',
    organizationId,
    isArtemAdmin,
  ] as const
}

async function getLlmChatProviderInfoApi(): Promise<LlmChatProviderInfoResponse> {
  const { data } = await apiClient.get<LlmChatProviderInfoResponse>(
    '/v1/llm-chat/provider-info'
  )
  return data
}

export function useLlmChatProviderInfo() {
  const sessionQuery = useGetUserSession()
  const organizationId = sessionQuery.data?.organizationId
  const isArtemAdmin = sessionQuery.data?.isArtemAdmin

  return useQuery({
    queryKey: llmChatProviderInfoQueryKey(organizationId, isArtemAdmin),
    queryFn: getLlmChatProviderInfoApi,
    enabled: sessionQuery.isSuccess && organizationId !== undefined,
    staleTime: Infinity,
  })
}

/**
 * Merges tool-only and text-only assistant turns into one bubble (tool cards first, then text).
 */
export function mergeAssistantToolAndTextParts(
  toolParts: UIMessage['parts'],
  textParts: UIMessage['parts']
): UIMessage['parts'] {
  return [...toolParts, ...textParts]
}

/**
 * Maps persisted Lex transcript rows to Vercel AI SDK {@link UIMessage} for `useChat`.
 */
export function llmChatRowsToUiMessages(
  messages: LlmChatMessageDto[]
): UIMessage[] {
  type StoredModelContentJson = {
    openAiToolCalls?: Array<{
      id?: string
      type?: string
      function?: {
        name?: string
        arguments?: string
      }
    }>
  }

  function parseJsonString(value: string | undefined | null): unknown {
    if (!value) {
      return {}
    }
    try {
      return JSON.parse(value)
    } catch {
      return { raw: value }
    }
  }

  function parseToolCallsFromContentJson(contentJson: unknown): Array<{
    id: string
    name: string
    input: unknown
  }> {
    const structured = contentJson as StoredModelContentJson | null
    const toolCalls = structured?.openAiToolCalls ?? []
    const result: Array<{ id: string; name: string; input: unknown }> = []
    for (const call of toolCalls) {
      if (call?.type !== 'function') {
        continue
      }
      const toolName = call.function?.name
      const toolCallId = call.id
      if (typeof toolName !== 'string' || typeof toolCallId !== 'string') {
        continue
      }
      result.push({
        id: toolCallId,
        name: toolName,
        input: parseJsonString(call.function?.arguments),
      })
    }
    return result
  }

  function parseToolOutput(contentText: string | null): unknown {
    if (!contentText || contentText.trim().length === 0) {
      return ''
    }
    try {
      return JSON.parse(contentText)
    } catch {
      return contentText
    }
  }

  const toolResultRowsByParent = new Map<string, LlmChatMessageDto[]>()
  for (const row of messages) {
    if (row.role !== 'TOOL_RESULT' || row.parentMessageId === null) {
      continue
    }
    const existing = toolResultRowsByParent.get(row.parentMessageId) ?? []
    existing.push(row)
    toolResultRowsByParent.set(row.parentMessageId, existing)
  }

  const raw: UIMessage[] = []
  for (const row of messages) {
    if (row.role === 'USER') {
      raw.push({
        id: row.id,
        role: 'user',
        parts: [{ type: 'text', text: row.contentText ?? '' }],
      })
      continue
    }
    if (row.role === 'MODEL') {
      const toolCalls = parseToolCallsFromContentJson(row.contentJson)
      const parts: UIMessage['parts'] = []
      if ((row.contentText ?? '').length > 0) {
        parts.push({ type: 'text', text: row.contentText ?? '' })
      }

      const pendingToolRows = [...(toolResultRowsByParent.get(row.id) ?? [])]
      for (const toolCall of toolCalls) {
        let linkedToolRow: LlmChatMessageDto | undefined
        const rowByExternalRef = pendingToolRows.find(
          (toolRow) =>
            toolRow.toolInvocationForResult?.externalInvocationRef ===
            toolCall.id
        )
        if (rowByExternalRef) {
          linkedToolRow = rowByExternalRef
        } else {
          linkedToolRow = pendingToolRows[0]
        }
        if (linkedToolRow) {
          const index = pendingToolRows.findIndex(
            (toolRow) => toolRow.id === linkedToolRow.id
          )
          if (index >= 0) {
            pendingToolRows.splice(index, 1)
          }
        }

        if (linkedToolRow) {
          parts.push({
            type: 'dynamic-tool',
            toolName: toolCall.name,
            toolCallId: toolCall.id,
            state: 'output-available',
            input: toolCall.input,
            output: parseToolOutput(linkedToolRow.contentText),
          })
        } else {
          parts.push({
            type: 'dynamic-tool',
            toolName: toolCall.name,
            toolCallId: toolCall.id,
            state: 'input-available',
            input: toolCall.input,
          })
        }
      }

      if (parts.length === 0) {
        parts.push({ type: 'text', text: row.contentText ?? '' })
      }

      raw.push({
        id: row.id,
        role: 'assistant',
        parts,
      })
      continue
    }
    if (row.role === 'SYSTEM') {
      raw.push({
        id: row.id,
        role: 'system',
        parts: [{ type: 'text', text: row.contentText ?? '' }],
      })
      continue
    }
    if (row.role === 'TOOL_RESULT') {
      // TOOL_RESULT rows are folded into their parent MODEL row as tool parts.
      continue
    }
  }

  /**
   * Merge consecutive tool-only assistant turns into the following text turn.
   *
   * A single agent response often spans several MODEL rows in the DB: one row
   * per tool-call step, followed by a final row with only the text reply.
   * Without merging, each row renders as its own bubble. We collapse every
   * run of consecutive tool-only bubbles that immediately precedes a
   * text-only bubble into that single text bubble. Tool cards render first,
   * then text. The merged bubble uses the text message's id so feedback is
   * attributed correctly.
   */
  function isToolOnlyAssistant(msg: UIMessage): boolean {
    return (
      msg.role === 'assistant' &&
      msg.parts.length > 0 &&
      msg.parts.every(
        (part) => part.type === 'dynamic-tool' || part.type.startsWith('tool-')
      )
    )
  }

  function isTextOnlyAssistant(msg: UIMessage): boolean {
    return (
      msg.role === 'assistant' &&
      msg.parts.length > 0 &&
      msg.parts.every((part) => part.type === 'text')
    )
  }

  const result: UIMessage[] = []
  let index = 0
  while (index < raw.length) {
    const current = raw[index]

    if (!isToolOnlyAssistant(current)) {
      result.push(current)
      index += 1
      continue
    }

    // Collect this and any subsequent consecutive tool-only assistant rows.
    const collectedToolParts: UIMessage['parts'] = [...current.parts]
    let lookahead = index + 1
    while (lookahead < raw.length && isToolOnlyAssistant(raw[lookahead])) {
      collectedToolParts.push(...raw[lookahead].parts)
      lookahead += 1
    }

    const nextMsg = raw[lookahead]
    if (nextMsg !== undefined && isTextOnlyAssistant(nextMsg)) {
      result.push({
        id: nextMsg.id,
        role: 'assistant',
        parts: mergeAssistantToolAndTextParts(
          collectedToolParts,
          nextMsg.parts
        ),
      })
      index = lookahead + 1
    } else {
      // No following text turn — emit the first tool-only bubble as-is
      // and let the loop advance naturally.
      result.push(current)
      index += 1
    }
  }
  return result
}

/**
 * Adapts a {@link UIMessage} from `useChat` to the DTO shape expected by {@link LlmChatMessageBubble}.
 */
export function llmChatUiMessageToDto(
  message: UIMessage,
  sessionId: string
): LlmChatMessageDto {
  const textParts = message.parts.filter(
    (part): part is { type: 'text'; text: string } =>
      part.type === 'text' &&
      typeof (part as { text?: unknown }).text === 'string'
  )
  const contentText = textParts.map((part) => part.text).join('')
  let role: LlmChatMessageRole
  if (message.role === 'user') {
    role = 'USER'
  } else if (message.role === 'assistant') {
    role = 'MODEL'
  } else {
    role = 'SYSTEM'
  }
  return {
    id: message.id,
    createdAt: '',
    updatedAt: '',
    sessionId,
    role,
    sequence: 0,
    parentMessageId: null,
    contentText,
    contentJson: null,
    usageJson: null,
    isComplete: true,
    systemPromptKey: null,
    systemPromptVersion: null,
    modelId: null,
    feedbackRating: null,
    feedbackReason: null,
    feedbackOtherComment: null,
    feedbackSubmittedByUserId: null,
    feedbackCreatedAt: null,
    feedbackUpdatedAt: null,
    toolInvocationIdForResult: null,
  }
}

async function fetchLlmChatSession(
  sessionId: string
): Promise<GetLlmChatSessionResponse> {
  const { data } = await apiClient.get<GetLlmChatSessionResponse>(
    `/v1/llm-chat/sessions/${sessionId}`
  )
  return data
}

export { fetchLlmChatSession }

async function listLlmChatSessionsApi(): Promise<ListLlmChatSessionsResponse> {
  const { data } = await apiClient.get<ListLlmChatSessionsResponse>(
    '/v1/llm-chat/sessions'
  )
  return data
}

export function useLlmChatSessionsList() {
  return useQuery({
    queryKey: llmChatSessionsListQueryKey(),
    queryFn: listLlmChatSessionsApi,
  })
}

export function useLlmChatSessionsForProduct(productId: string | undefined) {
  const query = useLlmChatSessionsList()

  const data = useMemo<ListLlmChatSessionsResponse | undefined>(() => {
    if (query.data === undefined || productId === undefined) {
      return query.data
    }

    return {
      sessions: query.data.sessions.filter(
        (session) => session.productId === productId
      ),
    }
  }, [productId, query.data])

  return {
    ...query,
    data,
  }
}

export async function createLlmChatSessionApi(input: {
  systemPromptKey: LlmChatSystemPromptKey
  productId?: string
}): Promise<CreateLlmChatSessionResponse> {
  const { data } = await apiClient.post<CreateLlmChatSessionResponse>(
    '/v1/llm-chat/sessions',
    input
  )
  return data
}

export function useCreateLlmChatSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createLlmChatSessionApi,
    onSuccess: () => {
      invalidateLlmChatSessionsList(queryClient)
    },
  })
}

export async function sendLlmChatMessageApi(input: {
  sessionId: string
  content: string
  maxTurns?: number
}): Promise<SendLlmChatMessageResponse> {
  const { data } = await apiClient.post<SendLlmChatMessageResponse>(
    `/v1/llm-chat/sessions/${input.sessionId}/messages`,
    {
      content: input.content,
      ...(input.maxTurns !== undefined && { maxTurns: input.maxTurns }),
    }
  )
  return data
}

export async function submitLlmChatMessageFeedbackApi(input: {
  sessionId: string
  messageId: string
  payload: SubmitLlmChatMessageFeedbackPayload
}): Promise<SubmitLlmChatMessageFeedbackResponse> {
  const { data } = await apiClient.patch<SubmitLlmChatMessageFeedbackResponse>(
    `/v1/llm-chat/sessions/${input.sessionId}/messages/${input.messageId}/feedback`,
    input.payload
  )
  return data
}

export function useSubmitLlmChatMessageFeedback(_sessionId: string) {
  return useMutation({
    mutationFn: (input: {
      messageId: string
      payload: SubmitLlmChatMessageFeedbackPayload
    }) =>
      submitLlmChatMessageFeedbackApi({
        sessionId: _sessionId,
        messageId: input.messageId,
        payload: input.payload,
      }),
  })
}

function llmChatSessionListSortKey(session: LlmChatSessionDto): number {
  return Date.parse(session.updatedAt)
}

export function mergeLlmChatSessionIntoList(
  sessions: ReadonlyArray<LlmChatSessionDto>,
  session: LlmChatSessionDto
): LlmChatSessionDto[] {
  const index = sessions.findIndex((item) => item.id === session.id)
  if (index === -1) {
    return [...sessions]
  }
  const current = sessions[index]!
  if (
    current.title === session.title &&
    current.summaryText === session.summaryText &&
    current.updatedAt === session.updatedAt
  ) {
    return sessions as LlmChatSessionDto[]
  }
  const nextSessions = [...sessions]
  nextSessions[index] = session
  nextSessions.sort(
    (left, right) =>
      llmChatSessionListSortKey(right) - llmChatSessionListSortKey(left)
  )
  return nextSessions
}

export function patchLlmChatSessionInListCache(
  queryClient: QueryClient,
  session: LlmChatSessionDto
) {
  queryClient.setQueryData<ListLlmChatSessionsResponse>(
    llmChatSessionsListQueryKey(),
    (current) => {
      if (!current) {
        return current
      }
      const nextSessions = mergeLlmChatSessionIntoList(
        current.sessions,
        session
      )
      if (nextSessions === current.sessions) {
        return current
      }
      return { sessions: nextSessions }
    }
  )
}

export function invalidateLlmChatSessionsList(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: llmChatSessionsListQueryKey(),
  })
}
